import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Text,
  SimpleGrid,
  Center,
  Flex,
  Box,
} from '@chakra-ui/react'
import { useQuery } from '@apollo/client'
import { useEffect } from 'react'
import { Spinner } from '@/components/atoms/Spinner'
import { StatsRow } from '@/components/atoms/StatsRow/StatsRow'
import { EXPLORERS } from '@/constants/constants'
import { GET_PLINKO_GAME } from '@/api/query'
import { FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import { CopiableLink } from '@/components/atoms/CopiableLink/CopiableLink'
import { UserGameResult } from '../UserGameResult/UserGameResult'
import { GameRisk, MiniGameStatus } from '@/__generated__/graphql'
import { getTokenByDenom } from '@/services/token-service'
import { formatAmount } from '@/helpers/formatAmount'
import { useTranslation } from 'next-i18next/pages'

export default ({ onClose, gameId }: { onClose: () => void; gameId: string }) => {
  const { t } = useTranslation('common')
  return (
    <Modal size={'lg'} isOpen={true} onClose={onClose}>
      <ModalOverlay />
      <ModalContent marginLeft={2.5} marginRight={2.5}>
        <ModalHeader pb={2} fontSize="xl" fontWeight="700">
          {t('common.game-details', { gameId })}
        </ModalHeader>
        <ModalCloseButton onClick={onClose} size="sm" />
        <ModalBody pb={5}>
          <PlinkoGameDetails gameId={gameId} />
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const PlinkoGameDetails = ({ gameId }: { gameId: string }) => {
  const { t } = useTranslation('common')
  const { data, refetch, error } = useQuery(GET_PLINKO_GAME, {
    variables: { id: gameId },
  })

  useEffect(() => {
    refetch()
  }, [])

  if (error) {
    return <Text color={'ninja.200'}>{t('common.error-occurred')}</Text>
  }

  if (!data?.plinkoGame) {
    return (
      <Center>
        <Spinner />
      </Center>
    )
  }

  const game = data.plinkoGame
  const token = getTokenByDenom(game.denom)

  // game.bet is total bet amount, calculate bet per ball
  const totalBet = game.bet * game.balls
  const multiplier = (parseFloat(game.payout) / totalBet).toFixed(2)

  const result = game.payout > totalBet ? MiniGameStatus.Win : MiniGameStatus.Lose

  return (
    <>
      <UserGameResult
        address={game.player}
        multiplier={multiplier}
        payout={{ amount: game.payout, denom: game.denom }}
        result={result}
      />
      <SimpleGrid columnGap={3} columns={{ base: 1, sm: 2 }}>
        <StatsRow name={t('common.game-status')} value={<Text color={'brand.green'}>{t('common.finished')}</Text>} />
        <StatsRow
          name={t('plinko.total-bet')}
          value={
            <FiatOrCryptoAmount
              iconSize={'18px'}
              denom={game.denom}
              value={formatAmount(totalBet.toString(), { decimals: token.decimals })}
              wrapperStyles={{ fontSize: 'xs' }}
            />
          }
        />
      </SimpleGrid>

      <SimpleGrid columnGap={3} columns={{ base: 1, sm: 2 }} mb={4}>
        <StatsRow
          name={t('common.tx-hash')}
          value={
            <CopiableLink
              trimmedLength={12}
              maxLength={32}
              link={`${EXPLORERS.INJECTIVE_TRANSACTIONS}${game.txHash}/`}
              value={game.txHash}
            />
          }
        />
        <StatsRow name={t('common.date')} value={<Text>{new Date(game.updated).toLocaleString()}</Text>} />
      </SimpleGrid>

      <PlinkoResultVisualization result={game.result as number[]} risk={game.risk} balls={game.balls} />
    </>
  )
}

const getRiskTranslationKey = (risk: GameRisk): string => {
  switch (risk) {
    case GameRisk.Low:
      return 'common.low'
    case GameRisk.Medium:
      return 'common.medium'
    case GameRisk.High:
      return 'common.high'
    default:
      return 'common.medium'
  }
}

const PlinkoResultVisualization = ({ result, risk, balls }: { result: number[]; risk: GameRisk; balls: number }) => {
  const { t } = useTranslation('common')
  // Count how many balls landed in each bucket (0-16, total 17 buckets)
  const bucketCounts = Array(17).fill(0)
  result.forEach((bucketIndex) => {
    if (bucketIndex >= 0 && bucketIndex < 17) {
      bucketCounts[bucketIndex]++
    }
  })

  // Get multipliers for each bucket based on risk level
  const multipliers = getMultipliers(risk)

  // Calculate max count for percentage calculation
  const maxCount = Math.max(...bucketCounts, 1)

  return (
    <Box>
      <Text fontSize={'sm'} fontWeight={700} color={'ninja.200'} mb={2}>
        {t('plinko.balls-distribution')}
      </Text>
      <Flex
        gap={{ base: 0.5, md: 1 }}
        justifyContent={'space-between'}
        bg={'ninja.700'}
        px={{ base: 1, md: 2 }}
        py={{ base: 2, md: 3 }}
        rounded={'2xl'}
        flexWrap={'nowrap'}
        overflow={'hidden'}
      >
        {bucketCounts.map((count, index) => {
          const hasCount = count > 0
          const color = getMultiplierColor(multipliers[index], risk)

          // Calculate percentage of total balls in this bucket
          const percentage = count / maxCount

          // Edge bonus: buckets near edges (0-2 and 14-16) get extra brightness
          // Center is bucket 8, so distance from center ranges 0-8
          const distanceFromCenter = Math.abs(8 - index)
          const edgeBonus = distanceFromCenter > 5 ? 0.2 : 0

          // Final opacity: base 0.3 + percentage (0-0.7) + edge bonus (0-0.2)
          const opacity = hasCount ? Math.min(0.3 + percentage * 0.7 + edgeBonus, 1) : 0.3

          return (
            <Flex
              key={index}
              flexDir={'column'}
              alignItems={'center'}
              gap={{ base: 0.5, md: 1 }}
              flex={'1 1 0'}
              minW={0}
              maxW={{ base: '30px', md: '40px' }}
            >
              <Box
                bg={hasCount ? color : 'ninja.600'}
                borderBottomRadius={{ base: 'md', md: 'lg' }}
                px={{ base: 0.5, md: 1 }}
                py={{ base: 1, md: 1.5 }}
                minH={{ base: '19px', md: '26px' }}
                display={'flex'}
                alignItems={'flex-end'}
                justifyContent={'center'}
                w={'full'}
                borderBottom={'1px solid'}
                borderLeft={'1px solid'}
                borderRight={'1px solid'}
                borderColor={hasCount ? color : 'ninja.500'}
                transition={'all 0.2s'}
                pb={{ base: 0.5, md: 1 }}
                opacity={opacity}
              >
                <Text fontSize={{ base: 'xs', md: 'md' }} fontWeight={700} color={'white'} lineHeight={1}>
                  {count}
                </Text>
              </Box>
              <Text fontSize={{ base: '9px', md: 'xs' }} fontWeight={600} color={'ninja.300'} lineHeight={1}>
                {index + 1}
              </Text>
            </Flex>
          )
        })}
      </Flex>
      <Flex mt={3} gap={2} fontSize={'xs'} color={'ninja.200'} justifyContent={'center'}>
        <Text>
          <Text as={'span'} color={'ninja.100'} fontWeight={700}>
            {balls}
          </Text>{' '}
          {balls === 1 ? t('plinko.ball') : t('plinko.balls')} {t('plinko.dropped')}
        </Text>
        <Text>•</Text>
        <Text>
          {t('plinko.risk')}:{' '}
          <Text as={'span'} color={'ninja.100'} fontWeight={700} textTransform={'capitalize'}>
            {t(getRiskTranslationKey(risk))}
          </Text>
        </Text>
      </Flex>
    </Box>
  )
}

// Multipliers for 17 buckets (16-row Plinko)
const getMultipliers = (risk: GameRisk): number[] => {
  switch (risk) {
    case GameRisk.Low:
      return [16, 9, 2, 1.4, 1.4, 1.2, 1.1, 1, 1, 1, 1.1, 1.2, 1.4, 1.4, 2, 9, 16]
    case GameRisk.Medium:
      return [110, 41, 10, 5, 3, 1.5, 1, 0.5, 0.5, 0.5, 1, 1.5, 3, 5, 10, 41, 110]
    case GameRisk.High:
      return [1000, 130, 26, 9, 4, 2, 0.2, 0.2, 0.2, 0.2, 0.2, 2, 4, 9, 26, 130, 1000]
    default:
      return Array(17).fill(1)
  }
}

const getMultiplierColor = (multiplier: number, risk: GameRisk): string => {
  if (risk === GameRisk.Low) {
    if (multiplier >= 16) return '#68F27F'
    if (multiplier >= 9) return '#73DE31'
    if (multiplier >= 2) return '#30C6AC'
    return '#8784B7'
  }

  if (risk === GameRisk.Medium) {
    if (multiplier >= 110) return '#C077F7'
    if (multiplier >= 41) return '#A26BF5'
    if (multiplier >= 10) return '#8E83FB'
    if (multiplier >= 5) return '#7C71F6'
    return '#8784B7'
  }

  if (risk === GameRisk.High) {
    if (multiplier >= 1000) return '#FF5757'
    if (multiplier >= 130) return '#FF7D7D'
    if (multiplier >= 26) return '#E383FB'
    if (multiplier >= 9) return '#C077F7'
    return '#8784B7'
  }

  return '#8784B7'
}
